/**
 * Domain Pricing Hook (P4-B3)
 *
 * Fetches and caches domain pricing from /api/pricing endpoint.
 * Handles loading states, errors, and fallbacks gracefully.
 */

import { useState, useEffect } from 'react';
import { fireEvent } from '@/lib/analytics';

interface PricingData {
  domain: string;
  binAmount: number; // cents
  ltoMonthly12: number; // cents
  ltoMonthly24: number; // cents
  ltoMonthly36: number; // cents
  currency: string;
}

interface UseDomainPricingResult {
  pricing: PricingData | null;
  loading: boolean;
  error: string | null;
  notConfigured: boolean;
}

/**
 * Fetch domain pricing with loading/error states
 */
export function useDomainPricing(domain: string): UseDomainPricingResult {
  const [pricing, setPricing] = useState<PricingData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notConfigured, setNotConfigured] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const fetchPricing = async () => {
      try {
        setLoading(true);
        setError(null);
        setNotConfigured(false);

        const response = await fetch(`/api/pricing?domain=${encodeURIComponent(domain)}`);
        const data = await response.json();

        if (cancelled) return;

        if (data.ok && data.data) {
          setPricing(data.data);
          setLoading(false);

          // Track successful price view
          fireEvent('price_viewed', {
            domain,
            binAmount: data.data.binAmount,
            ltoMonthly12: data.data.ltoMonthly12,
            ltoMonthly24: data.data.ltoMonthly24,
            ltoMonthly36: data.data.ltoMonthly36,
          });
        } else if (data.code === 'price_not_found') {
          // Not an error - just not configured yet
          setNotConfigured(true);
          setLoading(false);

          // Track missing price configuration (for ops)
          fireEvent('price_load_failed', {
            domain,
            error_code: 'price_not_found',
          });
        } else {
          throw new Error(data.message || 'Failed to fetch pricing');
        }
      } catch (err) {
        if (cancelled) return;

        const message = err instanceof Error ? err.message : 'Failed to load pricing';
        setError(message);
        setLoading(false);

        // Track error
        fireEvent('price_load_failed', {
          domain,
          error_code: 'fetch_error',
        });
      }
    };

    if (domain) {
      fetchPricing();
    }

    return () => {
      cancelled = true;
    };
  }, [domain]);

  return { pricing, loading, error, notConfigured };
}

/**
 * Format cents to USD display string
 */
export function formatPrice(cents: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(cents / 100);
}

/**
 * Format monthly price for LTO display
 */
export function formatMonthlyPrice(cents: number, months: number): string {
  const perMonth = formatPrice(cents);
  const total = formatPrice(cents * months);
  return `${perMonth}/mo × ${months} = ${total}`;
}

